"use client";

import { getInnerQueryData } from "@nimble/react-requests/data/response-utils";
import { useUrlDrawer } from "@nimble/react-state-hooks/use-url-drawer";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { truncateToPreviewLength } from "@/app/(discover)/candidate/invites/lib/invites-page.utils";
import {
  AlertsEmailDescription,
  AlertsGuestDescription,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/alert-components.view";
import { ALERTS_DRAWER_PARAM } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/alerts-drawer";
import { AlertsModalView } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/alerts-modal.view";
import {
  JOB_ALERTS_FORM_DEFAULTS,
  JobAlertsFormValues,
  toJobAlertsFormValues,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/job-alerts-form";
import { SavedSearchAlertRow } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/saved-searches-section";
import { usePreferenceFilters } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/use-preference-filters";
import {
  hasMeaningfulPreferences,
  summarizeFilters,
  toFilterValues,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/utils";
import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import { useGetJobAlerts } from "@/hooks/data/use-job-alerts";
import { useJobAlertsMutation } from "@/hooks/data/use-job-alerts-mutation";
import {
  GetSavedSearchesResponse,
  SavedSearchesParams,
  useUserSavedSearches,
} from "@/hooks/data/use-user-saved-searches";
import { User } from "@/lib/types/auth";

const PREFERENCES_EMPTY_SUBTITLE = "Not set yet — finish the quiz";
const SAVE_SUCCESS_MESSAGE = "Alerts saved";
const SAVE_FAILED_MESSAGE = "We couldn't save your alerts. Try again.";

const ALL_STATES_SAVED_SEARCHES: SavedSearchesParams = { scope: "all" };

const toSavedSearchAlertRows = (
  data: GetSavedSearchesResponse["data"] | null,
): SavedSearchAlertRow[] => {
  if (!data?.searches) {
    return [];
  }

  return data.searches.map((search) => ({
    uuid: search.uuid,
    title: search.name,
    subtitle: summarizeFilters(toFilterValues(search.filters)),
  }));
};

interface AlertsModalProps {
  open: boolean;
  onClose: () => void;
}

const AlertsModalAuthenticated = ({
  open,
  onClose,
  user,
}: AlertsModalProps & { user: User }) => {
  const jobAlertsQuery = useGetJobAlerts(user.id, { enabled: open });
  const alerts = getInnerQueryData(jobAlertsQuery);
  const values = useMemo(() => toJobAlertsFormValues(alerts), [alerts]);

  const form = useForm<JobAlertsFormValues>({
    defaultValues: JOB_ALERTS_FORM_DEFAULTS,
    values,
  });

  const { preferenceFilters } = usePreferenceFilters();

  const preferencesSubtitle = truncateToPreviewLength(
    preferenceFilters && hasMeaningfulPreferences(preferenceFilters)
      ? summarizeFilters(preferenceFilters)
      : PREFERENCES_EMPTY_SUBTITLE,
  );

  const savedSearchesQuery = useUserSavedSearches(
    user.id,
    ALL_STATES_SAVED_SEARCHES,
    { enabled: open },
  );
  const savedSearches = toSavedSearchAlertRows(
    getInnerQueryData(savedSearchesQuery),
  );

  const { mutate: saveSearch, isPending } = useJobAlertsMutation(user.id);

  const closeAndDiscard = () => {
    onClose();
    form.reset(values);
  };

  const handleSubmit = form.handleSubmit((data) => {
    form.clearErrors("root");
    saveSearch(data, {
      onSuccess: () => {
        closeAndDiscard();
        toast.success(SAVE_SUCCESS_MESSAGE);
      },
      onError: () => {
        form.setError("root", { message: SAVE_FAILED_MESSAGE });
      },
    });
  });

  return (
    <AlertsModalView
      open={open}
      onClose={closeAndDiscard}
      form={form}
      onSubmit={handleSubmit}
      description={<AlertsEmailDescription email={user.email} />}
      preferencesSubtitle={preferencesSubtitle}
      savedSearches={savedSearches}
      savedSearchesQueries={[savedSearchesQuery]}
      isSaving={isPending}
    />
  );
};

const AlertsModalGuest = ({ open, onClose }: AlertsModalProps) => {
  const form = useForm<JobAlertsFormValues>({
    defaultValues: JOB_ALERTS_FORM_DEFAULTS,
  });

  const closeAndDiscard = () => {
    onClose();
    form.reset(JOB_ALERTS_FORM_DEFAULTS);
  };

  return (
    <AlertsModalView
      open={open}
      onClose={closeAndDiscard}
      form={form}
      onSubmit={(event) => {
        event.preventDefault();
        closeAndDiscard();
      }}
      description={<AlertsGuestDescription />}
      preferencesSubtitle={PREFERENCES_EMPTY_SUBTITLE}
      savedSearches={[]}
      savedSearchesQueries={[]}
      isSaving={false}
    />
  );
};

const AlertsModal = withAuthGate(AlertsModalAuthenticated, {
  customLoader: null,
  customPaywall: (props) => <AlertsModalGuest {...props} />,
});

export const AlertsModalContainer = () => {
  const { isOpen, closeDrawer } = useUrlDrawer({
    paramName: ALERTS_DRAWER_PARAM,
  });

  return <AlertsModal open={isOpen} onClose={closeDrawer} />;
};
